import React, { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
  showRetryButton?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retryCount: number;
}

/**
 * Specialized error boundary for compliance-related components
 * Provides user-friendly error recovery options and detailed error reporting
 */
export class ComplianceErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;
  
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging and monitoring
    console.error('[ComplianceErrorBoundary] Compliance error caught:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });
    
    // Send error to monitoring service in production
    if (import.meta.env.PROD) {
      // Example: Sentry.captureException(error, { extra: errorInfo });
      console.warn('[ComplianceErrorBoundary] Error reporting not configured');
    }
  }

  handleRetry = () => {
    const { onRetry } = this.props;
    const { retryCount } = this.state;
    
    if (retryCount >= this.maxRetries) {
      console.warn(`[ComplianceErrorBoundary] Max retries (${this.maxRetries}) reached`);
      return;
    }
    
    console.log(`[ComplianceErrorBoundary] Retry attempt ${retryCount + 1}/${this.maxRetries}`);
    
    // Reset error state and increment retry count
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: retryCount + 1 
    });
    
    // Call custom retry function if provided
    if (onRetry) {
      try {
        onRetry();
      } catch (retryError) {
        console.error('[ComplianceErrorBoundary] Retry function failed:', retryError);
      }
    }
  };

  handleGoHome = () => {
    // Navigate to dashboard as fallback
    window.location.href = '/dashboard';
  };

  render() {
    const { hasError, error, retryCount } = this.state;
    const { children, fallback, showRetryButton = true } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Determine error type for better user messaging
      const isNetworkError = error?.message?.includes('fetch') || error?.message?.includes('Network');
      const isAuthError = error?.message?.includes('401') || error?.message?.includes('Unauthorized');
      const isValidationError = error?.message?.includes('validation') || error?.message?.includes('Invalid');
      
      const canRetry = retryCount < this.maxRetries && showRetryButton;
      
      return (
        <div className="flex items-center justify-center min-h-[400px] p-6">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 rounded-full bg-red-100 dark:bg-red-900/20 w-fit">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-xl">Compliance System Error</CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Error message based on type */}
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {isNetworkError && "Unable to load compliance data. Please check your connection."}
                  {isAuthError && "Your session has expired. Please refresh the page and log in again."}
                  {isValidationError && "There was an issue processing the compliance data."}
                  {!isNetworkError && !isAuthError && !isValidationError && 
                    "An unexpected error occurred while loading compliance information."
                  }
                </AlertDescription>
              </Alert>
              
              {/* Technical error details in development */}
              {import.meta.env.DEV && error && (
                <details className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded">
                  <summary className="cursor-pointer font-medium mb-2">
                    Technical Details (Development)
                  </summary>
                  <pre className="whitespace-pre-wrap break-words text-red-600 dark:text-red-400">
                    {error.message}
                    {error.stack && `\n\nStack trace:\n${error.stack.slice(0, 500)}`}
                  </pre>
                </details>
              )}
              
              {/* Action buttons */}
              <div className="flex flex-col gap-2 pt-2">
                {canRetry && (
                  <Button 
                    onClick={this.handleRetry} 
                    variant="default" 
                    className="w-full"
                    disabled={retryCount >= this.maxRetries}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Try Again {retryCount > 0 && `(${this.maxRetries - retryCount} attempts left)`}
                  </Button>
                )}
                
                <Button 
                  onClick={this.handleGoHome} 
                  variant="outline" 
                  className="w-full"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Go to Dashboard
                </Button>
                
                {retryCount >= this.maxRetries && (
                  <p className="text-sm text-muted-foreground text-center mt-2">
                    Multiple attempts failed. Please refresh the page or contact support.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return children;
  }
}

/**
 * Hook version of ComplianceErrorBoundary for functional components
 * Note: This is a simplified version - actual error boundaries must be class components
 */
export function useComplianceErrorHandler() {
  const handleError = React.useCallback((error: Error, errorInfo?: any) => {
    console.error('[useComplianceErrorHandler] Error caught:', error, errorInfo);
    
    // In a real implementation, you might want to:
    // - Show a toast notification
    // - Send error to monitoring service  
    // - Set error state in a global store
    
    if (import.meta.env.PROD) {
      console.warn('[useComplianceErrorHandler] Error reporting not configured');
    }
  }, []);
  
  return handleError;
}